import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import showToast from "../../utils/toastNotifications";
import { useThunk } from "../../hooks/useThunk";

import Top10Table from "../../components/Table/Top10Table";
import TrackSelectorModal from "../../components/Modals/TrackSelectorModal";

import assignIcon from "../../assets/icons/assign.svg";
import CustomButton from "../../components/Buttons/CustomButton";
import {
  getAllTracks,
  getTop10Tracks,
  getTop10TracksForYou,
  updateTop10Tracks,
  searchTracks, 
 
} from "../../services/thunks/top10EditsThunks";
import { twMerge } from "tailwind-merge";

const ManageTop10Edits = () => {
  const auth = useSelector((state) => state.auth);

  const [doGetTop10Tracks] = useThunk(getTop10Tracks);
  const [doUpdateTracks, isLoadingUpdate] = useThunk(updateTop10Tracks);
  const [doSearchTracks] = useThunk(searchTracks); 
  const [doGetTracksPaginated] = useThunk(getAllTracks);
  const [doGetTop10TracksForYou] = useThunk(getTop10TracksForYou);

  // Modal states
  const [showTrackSelector, setShowTrackSelector] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);

  // Track data states
  const [supremeTracks, setSupremeTracks] = useState(Array(10).fill(null));
  const [forYouTracks, setForYouTracks] = useState(Array(10).fill(null));
  const [loading, setLoading] = useState(true);

  const supremeTableRef = useRef(null);
  const forYouTableRef = useRef(null);

  useEffect(() => {
    loadExistingTracks();
  }, []);

  // Load existing top 10 tracks
  const loadExistingTracks = async () => {
    try {
      setLoading(true);
      const result = await doGetTop10Tracks({ token: auth.token });
      const forYouResult = await doGetTop10TracksForYou({ token: auth.token });

      if (result.success && result.response?.data) {
        const data = result.response.data.items;
        const newSupremeTracks = Array(10).fill(null);

        if (data && Array.isArray(data)) {
          data.forEach((track, index) => {
            if (index < 10 && track) {
              newSupremeTracks[index] = track;
            }
          });
        }

        setSupremeTracks(newSupremeTracks);
      }

      if (forYouResult.success && forYouResult.response?.data) {
        const forYouData = forYouResult.response.data.items;
        const newForYouTracks = Array(10).fill(null);

        if (forYouData && Array.isArray(forYouData)) {
          forYouData.forEach((track, index) => {
            if (index < 10 && track) {
              newForYouTracks[index] = track;
            }
          });
        }

        setForYouTracks(newForYouTracks);
      }
    } catch (error) {
      console.error("Error loading existing tracks:", error);
      showToast("error", "Failed to load existing tracks");
    } finally {
      setLoading(false);
    }
  };

  // Handle paginated track loading for modal
  const handleLoadTrackPage = async (pageSize, pageNum) => {
    try {
      console.log("Loading tracks for page:", pageNum);
      const result = await doGetTracksPaginated({
        token: auth.token,
        page: pageNum,
        limit: pageSize,
      });

      if (result.success && result.response?.data) {
        return {
          tracks: result.response.data.tracks || [],
          total: result.response.data.total || 0,
          hasMore: result.response.data.hasMore ?? false,
        };
      }

      return { tracks: [], total: 0, hasMore: false };
    } catch (error) {
      console.error("Error loading tracks:", error);
      return { tracks: [], total: 0, hasMore: false };
    }
  };

  // Handle search in modal
  const handleSearchTracks = async (searchTerm, pageSize, pageNum) => {
    try {
      const result = await doSearchTracks({
        token: auth.token,
        search: searchTerm,
        page: pageNum,
        limit: pageSize,
      });

      if (result.success && result.response?.data) {
        return {
          tracks: result.response.data.tracks || [],
          total: result.response.data.total || 0,
          hasMore: result.response.data.hasMore ?? false,
        };
      }

      return { tracks: [], total: 0, hasMore: false };
    } catch (error) {
      console.error("Error searching tracks:", error);
      return { tracks: [], total: 0, hasMore: false };
    }
  };

  // Handle track assignment
  const handleAssignTrack = (slot, section) => {
    setSelectedSlot(slot);
    setSelectedSection(section);
    setShowTrackSelector(true);
  };

  // Handle track selection from modal
  const handleTrackSelect = (track) => {
    const targetTracks =
      selectedSection === "supreme" ? supremeTracks : forYouTracks;
    const setTargetTracks =
      selectedSection === "supreme" ? setSupremeTracks : setForYouTracks;

    const isAlreadyAssigned = targetTracks.some(
      (t) => t && t._id === track._id
    );
    if (isAlreadyAssigned) {
      showToast("error", "Track is already assigned to this list");
      return;
    }

    // Update the specific slot
    const newTracks = [...targetTracks];
    newTracks[selectedSlot] = track;
    setTargetTracks(newTracks);

    setShowTrackSelector(false);
    setSelectedSlot(null);
    setSelectedSection(null);

    showToast("success", `Track assigned to position ${selectedSlot + 1}`);
  };

  // Check if all positions are filled
  const isFormValid = () => {
    const supremeFilled = supremeTracks.every((track) => track !== null);
    const forYouFilled = forYouTracks.every((track) => track !== null);
    return supremeFilled && forYouFilled;
  };

  // Handle save
  const handleSave = async () => {
    if (!isFormValid()) {
      showToast("error", "Please fill all 10 positions in both sections");
      return;
    }

    const payload = {
      supremeEssentials: supremeTracks.map((track) => ({
        id: track._id,
        trackId: track.trackId,
        editTitle: track.editTitle,
        versionType: track.versionType,
      })),

      forYou: forYouTracks.map((track) => ({
        id: track._id,
        trackId: track.trackId,
        editTitle: track.editTitle,
        versionType: track.versionType,
      })),
    };

    const result = await doUpdateTracks({
      token: auth.token,
      ...payload,
    });

    if (result.success) {
      showToast("success", "Top tracks updated successfully");
      loadExistingTracks();
    } else {
      showToast("error", result.error?.message || "Failed to update tracks");
    }
  };

  // Create table data for Top10Table
  const supremeTableData = useMemo(() => {
    return supremeTracks.map((track, index) => ({
      _id: track ? track._id : `empty-supreme-${index}`,
      position: index + 1,
      title: track ? track.title : null,
      artist: track ? track.artists : null,
      trackId: track ? track.trackId : null,
      isAssigned: track !== null,
      trackData: track,
      section: "supreme",
      slot: index,
    }));
  }, [supremeTracks]);

  const forYouTableData = useMemo(() => {
    return forYouTracks.map((track, index) => ({
      _id: track ? track._id : `empty-foryou-${index}`,
      position: index + 1,
      title: track ? track.title : null,
      artist: track ? track.artists : null,
      trackId: track ? track.trackId : null,
      isAssigned: track !== null,
      trackData: track,
      section: "foryou",
      slot: index,
    }));
  }, [forYouTracks]);

  // Table columns configuration
  const tableColumns = useMemo(
    () => [
      {
        title: <div style={{ paddingLeft: "2.375rem" }}>Rank</div>,
        dataIndex: "position",
        key: "position",
        minWidth: "100px",
        render: (text) => (
          <div className="min-h-[32px] flex items-center text-[20px] font-[700] font-inter text-[#E2E2E2] pl-[2.375rem]">
            {text}
          </div>
        ),
      },
      {
        title: "Track",
        dataIndex: "title",
        key: "title",
        minWidth: "300px",
        render: (text, record) => (
          <div className="min-h-[32px] flex items-center text-sm font-[500] font-inter text-[#E2E2E2]">
            {record.isAssigned ? (
              <span>{text}</span>
            ) : (
              <span className=" italic">Not Assigned</span>
            )}
          </div>
        ),
      },
      {
        title: "Artist(s)",
        dataIndex: "artist",
        key: "artist",
        minWidth: "200px",
        render: (text, record) => (
          <div className="min-h-[32px] flex items-center text-sm font-[500] font-['Inter'] text-[#E2E2E2]">
            {record.isAssigned ? text : "—"}
          </div>
        ),
      },
      {
        title: "Action",
        key: "action",
        minWidth: "180px",
        render: (_, record) => (
          <div className="flex gap-[8px] justify-start items-center">
            <button
              onClick={() => handleAssignTrack(record.slot, record.section)}
              className={twMerge(
                "flex items-center justify-center gap-[5px]  border rounded-[15px] text-[12px] w-[85px] h-7 font-inter",
                record.isAssigned
                  ? "border-[#939393] text-[#939393]"
                  : "border-[#52FAF0] text-[#52FAF0]"
              )}>
              {!record.isAssigned && (
                <img
                  src={assignIcon}
                  alt="assign"
                  className="w-[10px] h-[11px]"
                />
              )}
              <span>{record.isAssigned ? "Replace" : "Assign"}</span>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Row click handler
  const handleRowClick = (record) => {
    console.log("Row clicked:", record);
  };

  if (loading) {
    return (
      <div className="px-6 py-10 text-white min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading tracks...</div>
      </div>
    );
  }

  return (
    <div className="px-6 py-10 text-white min-h-screen bg-[#111111D6]">
      {/* Header */}
      <div className="flex justify-end items-center mb-8">
        <div className="flex gap-4">
          <CustomButton
            useGradientBorder
            rounded="rounded-lg"
            textSize="text-[18px] leading-[24px]"
            padding="py-[10.5px] px-[13.5px]"
            font="font-public-sans"
            border="border-lg"
            type="button"
            width="w-[154px] h-[45px]"
            onClick={handleSave}
            disabled={!isFormValid() || isLoadingUpdate}
          >
            Save Changes
          </CustomButton>
        </div>
      </div>

      {/* Supreme Essentials Section */}
      <div className="mb-12 bg-[#1A1A1A] p-6 rounded-lg border border-[#505050]">
        <h2 className="text-xl font-bold mb-6 text-[#E3E3E3] font-public-sans">
          Supreme Essentials – Top 10 Tracks
        </h2>
        <div ref={supremeTableRef}>
          <Top10Table
            data={supremeTableData}
            columns={tableColumns}
            loading={loading}
            headingBgColor="#0D141B"
            headingTextColor="#FFFFFF"
            onRowClick={handleRowClick}
            tableStyle={{
              background: "#191919",
              border: "1px solid #505050",
            }}
          />
        </div>
      </div>

      {/* For You Section */}
      <div className="mb-8 bg-[#1A1A1A] p-6 rounded-lg border border-[#505050]">
        <h2 className="text-xl font-bold mb-6 text-[#E3E3E3] font-public-sans">
          For You – Top 10 Tracks
        </h2>
        <div ref={forYouTableRef}>
          <Top10Table
            data={forYouTableData}
            columns={tableColumns}
            loading={loading}
            headingBgColor="#0D141B"
            headingTextColor="#FFFFFF"
            onRowClick={handleRowClick}
            tableStyle={{
              background: "#191919",
              border: "1px solid #505050",
            }}
          />
        </div>
      </div>

      {/* Track Selector Modal */}
      {showTrackSelector && (
        <TrackSelectorModal
          visible={showTrackSelector}
          onClose={() => {
            setShowTrackSelector(false);
            setSelectedSlot(null);
            setSelectedSection(null);
          }}
          onSelect={handleTrackSelect}
          onLoadPage={handleLoadTrackPage}
          onSearch={handleSearchTracks}
          title={`Assign Track to Slot ${selectedSlot + 1}`}
          excludeIds={
            selectedSection === "supreme"
              ? supremeTracks.filter((t) => t).map((t) => t._id)
              : forYouTracks.filter((t) => t).map((t) => t._id)
          }
        />
      )}
    </div>
  );
};

export default ManageTop10Edits;